/**
 * One inference at a time, across every onnxruntime session.
 *
 * Detaching recognition from the frame loop let the detector's `session.run` and the
 * embedder's `session.run` overlap — and onnxruntime-web's WebGPU backend is not
 * re-entrant across sessions: the shared BufferManager raced its readback
 * (`MapAsyncStatus::Success was false`), corrupted the WASM heap, and every call
 * afterwards died with "Out of bounds memory access" until reload. The same
 * non-reentrancy is why session *builds* are already serialised in assets.ts.
 *
 * So the runs are serialised too. This costs the frame loop a short wait when an embed
 * is in flight (~50-150 ms, once per read) and keeps everything else about the detached
 * read — the ORB worker round trip, the descriptor range fetches, the CPU stages —
 * overlapping with detection, which was the point of detaching it.
 */

let chain: Promise<unknown> = Promise.resolve();

/** Run `work` once every earlier locked call has finished. */
export function withOrtLock<T>(work: () => Promise<T>): Promise<T> {
  const next = chain.then(work, work);
  // A failed inference must not poison the queue for the one behind it.
  chain = next.catch(() => undefined);
  return next;
}

/**
 * Release the native storage behind inference tensors as soon as their data is consumed.
 *
 * This matters on WebGPU in particular: onnxruntime cannot infer JavaScript ownership,
 * so an output it created stays registered with the GPU buffer manager until `dispose()`
 * is called. Waiting for Safari's garbage collector lets those per-frame buffers pile up
 * until iOS terminates and reloads the page.
 */
export function disposeTensors(...tensors: Array<{ dispose(): void } | null | undefined>): void {
  const seen = new Set<{ dispose(): void }>();
  for (const tensor of tensors) {
    if (!tensor || seen.has(tensor)) continue;
    seen.add(tensor);
    tensor.dispose();
  }
}
